// <purpose>THE bottom bar of an open or decided row, letter flow and triage alike (decide, then
// step; decision 2026-10-28), and the after-press card that rises above the pressed slot. Three
// fixed slots, equal thirds of the bar; when a slot's act changes its LABEL cross-fades inside the
// same button (150 ms), and a slot that empties fades out and keeps its width — so no press ever
// shows a spinner, a blank bar or fewer than three slots. Registry: design/design-languages.html,
// entries `verb_buttons` and `row.settled_ghost` (the after-press card).</purpose>
// <dependencies>services/pressBar (the pure model: which act stands in which slot), DeclineSlot
// (slot 3 on an open row, its chooser), shared icons and theme tokens. The host maps each act to
// its mutation and owns the card's state; this file only draws and dispatches.</dependencies>
// <constraints>Layout law: the card, the caret list and a refusal message are
// all layers above the bar — none of them moves a slot or the pane. German strings are the act's
// own words (code-owned); kind words arrive served inside the model. No decorative glyphs.</constraints>
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, RetryIcon, CrossIcon } from './shared';
import DeclineSlot from './DeclineSlot';
import type { BarSlot, BarSlots, SlotAct, SlotItem, SlotOneFaces } from '../services/pressBar';
import { SLOT_TWO_FACES, SLOT_THREE_FACES } from '../services/pressBar';
import type { DeclineInstead } from '../types';
import { theme } from '../theme';

/** The after-press card as the host describes it (the host owns the state and the acts). */
export interface AfterPressView {
  /** `full` right after the press; `line` once the clerk moved on or chose to stay. */
  mode: 'full' | 'line';
  /** The decided mail: when it is the one on screen, its footer verdict carries the undo. */
  uuid?: string;
  /** „Genehmigt", „Weiterleitung genehmigt", „Nicht hilfreich · Kein Vorgang". */
  verdictText: string;
  subject: string;
  /** Above the pressed slot: slot 1 for the yes, slot 3 for the no. */
  side: 'left' | 'right';
  /** Rückgängig still stands (30 s from the press). */
  undoLive: boolean;
  /** Nächste Mail — absent where there is no list to step through (the pop-out). */
  onNext?: () => void;
  onUndo: () => void;
  onStay: () => void;
  /** WIM: the other letter, approved with A while the card stands. */
  nextKind?: { label: string; onPress: () => void } | null;
}

export const CARD_WORDS = {
  next: 'Nächste Mail',
  undo: 'Rückgängig',
  stay: 'Hier bleiben',
} as const;

const LABEL_FADE_MS = 150;
// Fixed columns, never sized by content (a slot that changes its act must not move its neighbours):
// slot 1 is the wider one because it carries the long verbs („Entwurf herunterladen",
// „Weiterleitung genehmigen") and the caret; slots 2 and 3 carry short ones.
export const SLOT_COLUMNS = 'minmax(0, 1.7fr) minmax(0, 1fr) minmax(0, 1fr)';
/** A slot button's side padding (shared with ActionButton's fill face, so slot 3 matches). */
export const SLOT_PADDING_X = 8;
const CARET_WIDTH = 30;
const ICON_SPACE = 14 + 6;
const SLOT_FONT = `500 13px ${theme.font.family}`;
const measureCache = new Map<string, number>();
/** The painted width of a label (canvas measureText); cached only once the font is loaded, so a
 *  reading taken with the fallback font is never kept. 7.5 px per character where no canvas exists. */
const labelPx = (text: string): number => {
  const ready = document.fonts?.check?.(SLOT_FONT) === true;
  if (ready && measureCache.has(text)) return measureCache.get(text)!;
  let px = text.length * 7.5;
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (ctx) { ctx.font = SLOT_FONT; const w = ctx.measureText(text).width; if (w > 0) px = w; }
  } catch { /* no canvas: the estimate stands */ }
  if (ready) measureCache.set(text, px);
  return px;
};
const faceNeed = (faces: { text: string; icon: boolean }[], caret = false): number =>
  Math.max(...faces.map(f => Math.ceil(labelPx(f.text)) + 2 * SLOT_PADDING_X + (f.icon ? ICON_SPACE : 0))) + (caret ? CARET_WIDTH : 0);
/** The bar's columns, each as wide a share as its longest face needs: whenever the three fit side
 *  by side, none truncates, and a row's columns never change with its state. */
export const slotColumns = (slotOne: SlotOneFaces | undefined): string => {
  if (!slotOne) return SLOT_COLUMNS;
  const w = [faceNeed(slotOne.labels, slotOne.caret), faceNeed(SLOT_TWO_FACES), faceNeed(SLOT_THREE_FACES)];
  return w.map(n => `minmax(0, ${n}fr)`).join(' ');
};
const SLOT_HEIGHT = 40;

// A slot's FACE signature: when it changes the slot cross-fades; when only the data behind the same
// face changes — a fresh closure, a re-read with the same words, a hold that disables it for a
// moment (the dim says that) — nothing moves.
const sigOf = (s: BarSlot): string => {
  if (s.type === 'blank') return 'blank';
  if (s.type === 'decline') return `decline:${s.noDraft}`;
  const m = s.main;
  return `act:${m.label}:${m.face ?? 'on'}:${(s.menu ?? []).map(i => i.label).join('|')}`;
};

const CaretIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width={14} height={14} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M4 10l4-4 4 4" fill="none" stroke={color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const iconOf = (icon: SlotItem['icon'], color: string) =>
  icon === 'check' ? <CheckIcon size={14} color={color} /> : icon === 'retry' ? <RetryIcon size={14} color={color} /> : null;

// The keycap the card and the caret list share (the decline chooser's hint face).
export const Keycap: React.FC<{ k: string }> = ({ k }) => (
  <span aria-hidden="true" style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 18, height: 18, padding: '0 4px', borderRadius: 4, fontSize: 10.5, fontWeight: 600,
    color: theme.colors.navyMid, backgroundColor: theme.colors.bgLight, flexShrink: 0, boxSizing: 'border-box',
  }}>{k}</span>
);

// ─── One slot's button (with the caret when it offers a list) ───────────────────────────────

const buttonBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: SLOT_HEIGHT, padding: `0 ${SLOT_PADDING_X}px`, boxSizing: 'border-box',
  fontSize: 13, fontWeight: 500, fontFamily: theme.font.family,
  color: theme.colors.white, backgroundColor: theme.colors.navy, border: 'none',
  whiteSpace: 'nowrap', minWidth: 0,
  transition: 'opacity 0.15s ease, background-color 0.15s ease',
};

const SlotButton: React.FC<{
  item: SlotItem; hasMenu: boolean;
  onPress: () => void; onCaret: () => void; menuOpen: boolean;
}> = ({ item, hasMenu, onPress, onCaret, menuOpen }) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  // The reason, when there is one (it can arrive while the button stands: a hold); else the full
  // verb, only when the column truncates it.
  const onEnter = () => {
    const el = labelRef.current;
    setTruncated(!!el && el.scrollWidth > el.clientWidth + 1);
  };
  const hoverTitle = item.title ?? (truncated ? item.label : undefined);
  const opacity = item.face === 'potential' ? 0.45 : item.enabled || item.undimmed ? 1 : 0.6;
  return (
    <div style={{ display: 'flex', width: '100%', minWidth: 0, opacity, transition: 'opacity 0.15s ease' }}>
      <button
        type="button"
        onClick={onPress}
        disabled={!item.enabled}
        title={hoverTitle}
        onMouseEnter={onEnter}
        data-testid="press-slot-main"
        style={{
          ...buttonBase, flex: 1,
          borderRadius: hasMenu ? `${theme.borderRadius.md}px 0 0 ${theme.borderRadius.md}px` : theme.borderRadius.md,
          cursor: item.enabled ? 'pointer' : 'not-allowed',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {iconOf(item.icon, theme.colors.white)}
          <span ref={labelRef} style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.label}</span>
        </span>
      </button>
      {hasMenu && (
        <button
          type="button"
          onClick={onCaret}
          aria-label="Weitere Aktionen"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          data-testid="press-slot-caret"
          style={{
            ...buttonBase, width: CARET_WIDTH, padding: 0, flexShrink: 0,
            borderLeft: '1px solid rgba(255,255,255,0.35)',
            borderRadius: `0 ${theme.borderRadius.md}px ${theme.borderRadius.md}px 0`,
            cursor: 'pointer',
          }}
        >
          <CaretIcon color={theme.colors.white} />
        </button>
      )}
    </div>
  );
};

// ─── The caret's list ───────────────────────────────────────────────────────────────────────

const SlotMenu: React.FC<{
  anchor: HTMLElement; items: SlotItem[]; heading?: string;
  onPick: (item: SlotItem) => void; onClose: () => void;
}> = ({ anchor, items, heading, onPick, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const rect = anchor.getBoundingClientRect();
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return; }
      const it = items.find(i => i.hotkey === e.key);
      if (it) { e.preventDefault(); e.stopPropagation(); if (it.enabled) onPick(it); }
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey, true); document.removeEventListener('mousedown', onDown); };
  }, [items, anchor, onPick, onClose]);
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={heading ?? 'Aktionen'}
      tabIndex={-1}
      data-testid="press-slot-menu"
      style={{
        position: 'fixed', bottom: Math.max(8, window.innerHeight - rect.top + 8),
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 328)),
        minWidth: Math.max(240, rect.width), maxWidth: 320,
        backgroundColor: theme.colors.white, border: `1.5px solid ${theme.colors.navy}`,
        borderRadius: theme.borderRadius.md, boxShadow: '0 8px 24px rgba(0,13,102,0.18)',
        padding: `${theme.spacing.sm}px 0`, zIndex: 950, /* above compare mode's stage (900), under modals */ outline: 'none', fontFamily: theme.font.family,
        animation: 'stx-cardRise 0.15s ease-out',
      }}
    >
      {heading && (
        <div style={{ padding: `2px ${theme.spacing.md}px ${theme.spacing.sm}px`, fontSize: 12, fontWeight: 600, color: theme.colors.navyMid }}>{heading}</div>
      )}
      {items.map((it, n) => (
        <button
          key={`${it.label}-${n}`}
          type="button"
          role="menuitem"
          disabled={!it.enabled}
          title={it.title}
          onClick={() => { if (it.enabled) onPick(it); }}
          style={{
            display: 'flex', alignItems: 'center', gap: theme.spacing.sm, width: '100%',
            padding: `6px ${theme.spacing.md}px`, border: 'none', background: 'transparent',
            cursor: it.enabled ? 'pointer' : 'not-allowed', textAlign: 'left', fontFamily: theme.font.family,
            fontSize: 13, fontWeight: 500, color: theme.colors.navy, opacity: it.enabled ? 1 : 0.5,
            borderTop: it.act.type === 'approveKind' && it.act.kind === 'archive' && n > 0 ? `1px solid ${theme.colors.borderLight}` : undefined,
          }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = theme.colors.bgLight; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <Keycap k={it.hotkey ?? ''} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>
        </button>
      ))}
    </div>,
    document.body,
  );
};

// ─── The after-press card ───────────────────────────────────────────────────────────────────

const CardItem: React.FC<{ k: string; label: string; onPress: () => void; highlighted?: boolean; hidden?: boolean; testId: string }> = ({ k, label, onPress, highlighted, hidden, testId }) => (
  <button
    type="button"
    onClick={onPress}
    data-testid={testId}
    tabIndex={hidden ? -1 : 0}
    aria-hidden={hidden || undefined}
    style={{
      display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: `5px ${theme.spacing.md - 4}px`,
      border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: theme.font.family, fontSize: 13,
      color: theme.colors.navy, backgroundColor: highlighted ? theme.colors.bgLight : 'transparent',
      opacity: hidden ? 0 : 1, pointerEvents: hidden ? 'none' : undefined, transition: 'opacity 0.2s ease',
    }}
  >
    <Keycap k={k} />{label}
  </button>
);

export const AfterPressCard: React.FC<{ view: AfterPressView }> = ({ view }) => {
  const heading = (
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
      {view.verdictText}: <span style={{ fontWeight: 500, color: theme.colors.textSecondary }}>{view.subject || '(Kein Betreff)'}</span>
    </span>
  );
  const box: React.CSSProperties = {
    position: 'absolute', bottom: `calc(100% + ${theme.spacing.sm}px)`,
    ...(view.side === 'right' ? { right: 0 } : { left: 0 }),
    width: 'min(340px, 100%)', boxSizing: 'border-box',
    backgroundColor: theme.colors.white, border: `1.5px solid ${theme.colors.navy}`, borderRadius: theme.borderRadius.md,
    boxShadow: '0 6px 22px rgba(0,13,102,0.18)', zIndex: 3, fontFamily: theme.font.family, fontSize: 12.5, color: theme.colors.navy,
    animation: 'stx-cardRise 0.15s ease-out',
  };
  if (view.mode === 'line') {
    return (
      <div role="status" data-testid="after-press-line" style={{ ...box, display: 'flex', alignItems: 'center', gap: 8, padding: `4px 4px 4px ${theme.spacing.md - 4}px` }}>
        {heading}
        <span style={{ flexShrink: 0, marginLeft: 'auto' }}>
          <CardItem k="Z" label={CARD_WORDS.undo} onPress={view.onUndo} hidden={!view.undoLive} testId="after-press-undo" />
        </span>
      </div>
    );
  }
  return (
    <div role="dialog" aria-label={`${view.verdictText}: ${view.subject}`} data-testid="after-press-card" style={{ ...box, padding: '10px 0 6px' }}>
      <div style={{ display: 'flex', padding: `0 ${theme.spacing.md - 4}px 6px`, minWidth: 0 }}>{heading}</div>
      {view.nextKind && <CardItem k="A" label={view.nextKind.label} onPress={view.nextKind.onPress} testId="after-press-next-kind" />}
      {view.onNext && <CardItem k="Enter" label={CARD_WORDS.next} onPress={view.onNext} highlighted testId="after-press-next" />}
      <CardItem k="Z" label={CARD_WORDS.undo} onPress={view.onUndo} hidden={!view.undoLive} testId="after-press-undo" />
      <CardItem k="Esc" label={CARD_WORDS.stay} onPress={view.onStay} testId="after-press-stay" />
    </div>
  );
};

// ─── The bar ────────────────────────────────────────────────────────────────────────────────

export interface PressBarProps {
  slots: BarSlots;
  onAct: (act: SlotAct) => void;
  decline: {
    status: string; previousStatus?: string | null; primaryAxis?: string | null;
    writesBlocked: boolean; maintenance: boolean;
    onDecline: (instead: DeclineInstead | null) => void | Promise<void>;
    /** Changes when the N key asks the chooser to open. */
    openRequest?: number;
  };
  card?: AfterPressView | null;
  /** A refused press, rolled back: said on the bar, where the press happened. */
  message?: string | null;
  onDismissMessage?: () => void;
  /** Every face slot 1 can wear on this row — sizes the columns once per row. */
  slotOne?: SlotOneFaces;
  /** Slot 1 is hovered (a disabled button fires no pointer events, so the cell reports it). */
  onSlotOneHover?: (hovering: boolean) => void;
}

// A slot's change of act is a CROSS-FADE: the old face stays on top and dissolves (150 ms) over the
// new one, so the button's ground never empties — no frame shows a blank slot or a missing one. A
// slot coming out of blank fades in; a slot going blank fades out and keeps its width. While the
// swap runs the slot takes no press, so a double click never lands on the next act.
const SlotCell: React.FC<{ slot: BarSlot; children: (shown: BarSlot, outgoing: boolean) => React.ReactNode; index: number }> = ({ slot, children, index }) => {
  const sig = sigOf(slot);
  const last = useRef<{ sig: string; slot: BarSlot }>({ sig, slot });
  const [outgoing, setOutgoing] = useState<{ slot: BarSlot; fromBlank: boolean; n: number } | null>(null);
  // Before paint, so the new face is never painted alone.
  useLayoutEffect(() => {
    const prev = last.current;
    if (prev.sig === sig) return undefined;
    setOutgoing(o => ({ slot: prev.slot, fromBlank: prev.slot.type === 'blank', n: (o?.n ?? 0) + 1 }));
    const t = setTimeout(() => setOutgoing(null), LABEL_FADE_MS);
    return () => clearTimeout(t);
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { last.current = { sig, slot }; });
  return (
    <div
      data-testid={`press-slot-${index + 1}`}
      data-slot-type={slot.type}
      style={{ position: 'relative', display: 'flex', minWidth: 0, height: SLOT_HEIGHT, pointerEvents: outgoing ? 'none' : undefined }}
    >
      <div key={outgoing?.fromBlank ? `in-${outgoing.n}` : 'steady'} style={{ display: 'flex', flex: 1, minWidth: 0, animation: outgoing?.fromBlank ? `stx-fadeIn ${LABEL_FADE_MS}ms ease` : undefined }}>
        {children(slot, false)}
      </div>
      {outgoing && outgoing.slot.type !== 'blank' && (
        <div key={`out-${outgoing.n}`} aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none', animation: `stx-fadeOut ${LABEL_FADE_MS}ms ease forwards` }}>
          {children(outgoing.slot, true)}
        </div>
      )}
    </div>
  );
};

export const PressBar: React.FC<PressBarProps> = ({ slots, onAct, decline, card, message, onDismissMessage, slotOne, onSlotOneHover }) => {
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const cellRefs = useRef<(HTMLDivElement | null)[]>([]);
  const closeMenu = useCallback(() => setMenuFor(null), []);
  const pick = useCallback((it: SlotItem) => { setMenuFor(null); onAct(it.act); }, [onAct]);
  return (
    <div data-testid="press-bar" style={{ position: 'relative' }}>
      {message && (
        <div role="alert" data-testid="press-bar-message" style={{
          position: 'absolute', left: 0, right: 0, bottom: `calc(100% + ${theme.spacing.sm}px)`, zIndex: 4,
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', fontSize: 13,
          backgroundColor: '#fef2f2', color: theme.colors.error, border: `1px solid ${theme.colors.error}`,
          borderRadius: theme.borderRadius.md, animation: 'stx-cardRise 0.15s ease-out', fontFamily: theme.font.family,
        }}>
          <span style={{ flex: 1, minWidth: 0 }}>{message}</span>
          {onDismissMessage && (
            <button type="button" aria-label="Meldung schließen" onClick={onDismissMessage} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', padding: 2 }}>
              <CrossIcon size={12} color={theme.colors.error} />
            </button>
          )}
        </div>
      )}
      {card && <AfterPressCard view={card} />}
      <div style={{ display: 'grid', gridTemplateColumns: slotColumns(slotOne), gap: theme.spacing.sm }}>
        {slots.map((slot, index) => (
          <div
            key={index}
            ref={el => { cellRefs.current[index] = el; }}
            style={{ minWidth: 0 }}
            onMouseEnter={index === 0 && onSlotOneHover ? () => onSlotOneHover(true) : undefined}
            onMouseLeave={index === 0 && onSlotOneHover ? () => onSlotOneHover(false) : undefined}
          >
            <SlotCell slot={slot} index={index}>
              {(shown, isOutgoing) => {
                if (shown.type === 'blank') return <span aria-hidden="true" style={{ flex: 1 }} />;
                if (shown.type === 'decline') {
                  return (
                    <DeclineSlot
                      // The outgoing layer keeps the open face it had: the row is already decided.
                      status={isOutgoing ? 'suggestion_ready' : decline.status} previousStatus={decline.previousStatus} primaryAxis={decline.primaryAxis}
                      noDraft={shown.noDraft} writesBlocked={decline.writesBlocked} maintenance={decline.maintenance}
                      onDecline={decline.onDecline} openRequest={decline.openRequest} fill
                    />
                  );
                }
                return (
                  <SlotButton
                    item={shown.main}
                    hasMenu={!!shown.menu}
                    menuOpen={menuFor === index}
                    onPress={() => onAct(shown.main.act)}
                    onCaret={() => setMenuFor(m => (m === index ? null : index))}
                  />
                );
              }}
            </SlotCell>
          </div>
        ))}
      </div>
      {menuFor !== null && slots[menuFor].type === 'act' && (slots[menuFor] as Extract<BarSlot, { type: 'act' }>).menu && cellRefs.current[menuFor] && (
        <SlotMenu
          anchor={cellRefs.current[menuFor]!}
          items={(slots[menuFor] as Extract<BarSlot, { type: 'act' }>).menu!}
          heading={(slots[menuFor] as Extract<BarSlot, { type: 'act' }>).menuHeading}
          onPick={pick}
          onClose={closeMenu}
        />
      )}
    </div>
  );
};

export default PressBar;
